import { supabase } from './supabaseClient';
import { Database } from '../../types/supabase';

type Campaign = Database['public']['Tables']['campaigns']['Row'];
type CampaignInsert = Database['public']['Tables']['campaigns']['Insert'];
type CampaignUpdate = Database['public']['Tables']['campaigns']['Update'];

/**
 * Récupère toutes les campagnes de l'utilisateur connecté
 */
export const getCampaigns = async (userId: string): Promise<{ data: Campaign[] | null; error: Error | null }> => {
  const { data, error } = await supabase
    .from('campaigns')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
    
  return { data, error: error as Error | null };
};

/**
 * Récupère une campagne par son ID
 * Vérifie également que l'utilisateur est le propriétaire
 */
export const getCampaignById = async (id: string, userId: string): Promise<{ data: Campaign | null; error: Error | null }> => {
  const { data, error } = await supabase
    .from('campaigns')
    .select('*')
    .eq('id', id)
    .eq('user_id', userId)
    .single();
  
  return { data, error: error as Error | null };
};

/**
 * Crée une nouvelle campagne
 */
export const createCampaign = async (campaign: Omit<CampaignInsert, 'user_id'>, userId: string): Promise<{ data: Campaign | null; error: Error | null }> => {
  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from('campaigns')
    .insert({
      ...campaign,
      user_id: userId,
      created_at: now,
      updated_at: now,
      database_statut: campaign.database_statut || 'En attente',
      linkedin_statut: campaign.linkedin_statut || 'En attente',
      mail_statut: campaign.mail_statut || 'En attente',
      lance: campaign.lance || false
    })
    .select()
    .single();
  
  return { data, error: error as Error | null };
};

/**
 * Met à jour une campagne existante
 * Vérifie d'abord que l'utilisateur est le propriétaire
 */
export const updateCampaign = async (id: string, updates: CampaignUpdate, userId: string): Promise<{ data: Campaign | null; error: Error | null }> => {
  // Vérifier d'abord que l'utilisateur est propriétaire de la campagne
  const { data: existingCampaign, error: checkError } = await getCampaignById(id, userId);
  
  if (checkError || !existingCampaign) {
    return { 
      data: null, 
      error: checkError || new Error('Campagne non trouvée ou accès non autorisé') 
    };
  }
  
  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from('campaigns')
    .update({
      ...updates,
      updated_at: now
    })
    .eq('id', id)
    .select()
    .single();
  
  return { data, error: error as Error | null };
};

/**
 * Supprime une campagne
 * Vérifie d'abord que l'utilisateur est le propriétaire
 */
export const deleteCampaign = async (id: string, userId: string): Promise<{ error: Error | null }> => {
  // Vérifier d'abord que l'utilisateur est propriétaire de la campagne
  const { data: existingCampaign, error: checkError } = await getCampaignById(id, userId);
  
  if (checkError || !existingCampaign) {
    return { 
      error: checkError || new Error('Campagne non trouvée ou accès non autorisé') 
    };
  }
  
  const { error } = await supabase
    .from('campaigns')
    .delete()
    .eq('id', id);
  
  return { error: error as Error | null };
};

/**
 * Met à jour le statut de lancement d'une campagne
 */
export const launchCampaign = async (id: string, launched: boolean, userId: string): Promise<{ data: Campaign | null; error: Error | null }> => {
  // Vérifier d'abord que l'utilisateur est propriétaire de la campagne
  const { data: existingCampaign, error: checkError } = await getCampaignById(id, userId);
  
  if (checkError || !existingCampaign) {
    return { 
      data: null, 
      error: checkError || new Error('Campagne non trouvée ou accès non autorisé') 
    };
  }
  
  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from('campaigns')
    .update({
      lance: launched,
      date_lancement: launched ? now : null,
      updated_at: now
    })
    .eq('id', id)
    .select()
    .single();
  
  return { data, error: error as Error | null };
};

/**
 * Met à jour les statuts de validation d'une campagne
 */
export const updateCampaignStatus = async (
  id: string, 
  updates: {
    database_statut?: 'En attente' | 'Validé';
    linkedin_statut?: 'En attente' | 'Validé';
    mail_statut?: 'En attente' | 'Validé';
  },
  userId: string
): Promise<{ data: Campaign | null; error: Error | null }> => {
  // Vérifier d'abord que l'utilisateur est propriétaire de la campagne
  const { data: existingCampaign, error: checkError } = await getCampaignById(id, userId);
  
  if (checkError || !existingCampaign) {
    return { 
      data: null, 
      error: checkError || new Error('Campagne non trouvée ou accès non autorisé') 
    };
  }
  
  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from('campaigns')
    .update({
      ...updates,
      updated_at: now
    })
    .eq('id', id)
    .select()
    .single();
  
  return { data, error: error as Error | null };
};

/**
 * Met à jour les données du questionnaire d'une campagne
 */
export const updateCampaignQuestionnaire = async (
  id: string,
  questionnaireData: Record<string, any>,
  userId: string
): Promise<{ data: Campaign | null; error: Error | null }> => {
  // Vérifier d'abord que l'utilisateur est propriétaire de la campagne
  const { data: existingCampaign, error: checkError } = await getCampaignById(id, userId);
  
  if (checkError || !existingCampaign) {
    return { 
      data: null, 
      error: checkError || new Error('Campagne non trouvée ou accès non autorisé') 
    };
  }
  
  const now = new Date().toISOString();
  const updateData: CampaignUpdate = {
    questionnaire_data: questionnaireData,
    updated_at: now
  };
  
  // Si un nom de campagne est fourni dans le questionnaire, mettre à jour le nom de la campagne
  if (questionnaireData.campaignName) {
    updateData.nom = questionnaireData.campaignName;
  }
  
  const { data, error } = await supabase
    .from('campaigns')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();
  
  return { data, error: error as Error | null };
};

export const updateEmailSentFlags = async (
  campaignId: string, 
  flags: {
    mail_copy_email_sent?: boolean;
    linkedin_copy_email_sent?: boolean;
    database_email_sent?: boolean;
  },
  userId: string
) => {
  const { data, error } = await supabase
    .from('campaigns')
    .update(flags)
    .eq('id', campaignId)
    .eq('user_id', userId)
    .select()
    .single();

  return { data, error };
}; 